import _ from 'lodash';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { ImportInKindItemsService } from 'app/import-in-kind-items/services/import-in-kind-items.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { selectInKindCampaignList } from 'app/campaign/features/campaign.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import * as moment from 'moment';
import { NgxPaginationModule } from 'ngx-pagination';
import { AuctionPresetOptionSchema } from 'app/campaign/structure/auction';
import { CampaignService } from 'app/campaign/services';
import { forkJoin } from 'rxjs';
import { AMOUNT_LIMIT } from 'app/virtual-event/virtual-event.enums';
import { InKindItemElementComponent } from '../in-kind-item-element/in-kind-item-element.component';
import { filter, switchMap, map, pairwise } from 'rxjs/operators';

@Component({
  selector: 'app-import-in-kind-items',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    MdbFormsModule, 
    ReactiveFormsModule, 
    MdbSelectModule, 
    NgxPaginationModule,
    InKindItemElementComponent
  ],
  templateUrl: './import-in-kind-items.component.html',
  styleUrl: './import-in-kind-items.component.scss'
})
export class ImportInKindItemsComponent implements OnInit{
  destroyRef = inject(DestroyRef)
  
  @Input () data

  public form: UntypedFormGroup;
  public transactionForm: UntypedFormGroup;

  public inKindCampaignList: any = [];

  public allTransactionList: any = [];
  public filteredTransactionList: any = [];
  
  public selectedItemsList: any = [];
  public isAllSelected: boolean = false;
  public isImportDisabled: boolean = true;

  public search: UntypedFormControl = new UntypedFormControl('', []);
  public pageNumber: number = 1;

  public showImages: boolean = false;
  public showDesc: boolean = false;
  public showCategories: boolean = false;

  public inKindCampaigns$: any = this.store.pipe(
    takeUntilDestroyed(this.destroyRef),
    select(selectInKindCampaignList),
  );

  constructor (
    private store: Store,
    private importInKindItemsService: ImportInKindItemsService,
    private fb: UntypedFormBuilder,
    private campaignService: CampaignService
  ) { }

  ngOnInit () {
    const formControls = {
      inKindDonationIds: [[], Validators.required],
    };
    this.form = this.fb.group(formControls);

    const inKindDonationIds = this.form.get('inKindDonationIds');

    this.inKindCampaigns$.pipe(
    ).subscribe(list => {
      this.inKindCampaignList = list;
    })

    // Handle 'all' selection
    inKindDonationIds.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(list => {
        return list.length < 1 ? false : list[list.length - 1] === "all";
      }),
      switchMap(() => {
        return this.inKindCampaigns$;
      }),
      map((campaigns: any[]) => {
        return campaigns.map(campaign => campaign.id);
      })
    ).subscribe((list) => {
      inKindDonationIds.setValue(["all", ...list], { emitEvent: false });
      this.loadTransactions(list);
      this.isAllSelected = true;
      this.updateImportButtonState();
    });

    inKindDonationIds.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(list => list.length < 2 ? false : list[0] === "all"),
      map(list => list.slice(1, list.length - 1))
    ).subscribe((list) => {
      inKindDonationIds.setValue(list, { emitEvent: false });
      this.loadTransactions(list);
      this.isAllSelected = true;
      this.updateImportButtonState();
    });

    inKindDonationIds.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      pairwise(),
      filter(([previousValue]) => {
        return previousValue.findIndex(element => element === "all") >= 0;
      }),
      filter(([,currentValue]) => {
        return currentValue.findIndex(element => element === "all") < 0;
      }),
      filter(([previousValue, currentValue]) => {
        return previousValue.length - currentValue.length < 2;
      })
    ).subscribe(() => {
      inKindDonationIds.setValue([], { emitEvent: false });
      this.loadTransactions([]);
      this.isAllSelected = false;
      this.updateImportButtonState();
    });

    // Handle regular selection (when 'all' is not involved)
    inKindDonationIds.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(list => !list.includes('all'))
    ).subscribe((list) => {
      this.loadTransactions(list);
      this.isAllSelected = this.allTransactionList.length > 0 && 
        this.allTransactionList.every(transaction => transaction.checked);
      this.updateImportButtonState();
    });

    this.search.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(search => {
      this.filteredTransactionList = _.filter(this.allTransactionList, (transaction) => {
        const { inKindDonationItemInfo } = transaction;
        return _.includes(inKindDonationItemInfo.itemName.toLowerCase(), search.toLowerCase()) ||
          _.includes(inKindDonationItemInfo.desc.toLowerCase(), search.toLowerCase());
      });

      this.pageNumber = 1;
    });
  }

  private loadTransactions(selectedIds: string[]) {
    if (!selectedIds.length) {
      this.allTransactionList = [];
      this.filteredTransactionList = [];
      this.selectedItemsList = [];
      this.isAllSelected = false;
      return;
    }

    const donationRequests = selectedIds.map(campaignId => {
      const params = {
        startDate: 0,
        endDate: 0,
        campaignId
      };
      return this.importInKindItemsService.getCampaignDonations(params);
    });

    forkJoin(donationRequests).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((donationLists: any[]) => {
      this.allTransactionList = donationLists.flat();

      this.allTransactionList.map(transaction => {
        transaction.checked = true;

        const itemValue = transaction?.inKindDonationItemInfo?.value || 0;
        transaction.minBid = itemValue;
        transaction.bidIncrement = Math.round(itemValue * 0.1);

        return transaction;
      });

      this.filteredTransactionList = this.allTransactionList;
      this.selectedItemsList = this.allTransactionList;
      this.isAllSelected = true;

      // check if transaction has images, desc or categories
      this.showImages = this.allTransactionList.some(donation => donation.inKindDonationItemInfo?.images?.length > 0);
      this.showDesc = this.allTransactionList.some(donation => donation.inKindDonationItemInfo?.desc !== '');
      this.showCategories = this.allTransactionList.some(donation => donation.inKindDonationItemInfo?.categories?.length > 0);

      this.updateImportButtonState();
    });
  }

  getItemData() {
    // map selectedItemsList to create an array of arrays of { field: '', value: '' }
    const itemData = this.selectedItemsList.map(item => {
      const info = item.inKindDonationItemInfo || null;
      if (!info) {
        return [];
      }

      const data = [];

      data.push({field: 'count', value: 1});
      data.push({field: 'name', value: info.itemName || ''})

      if (item?.payer?.name) {
        data.push({field: 'donatedByName', value: item.payer.name})
        data.push({field: 'displayDonatedBy', value: true})
      }

      const itemValue = info.value || 0;
      data.push({field: 'valueProvided', value: itemValue})

      const minBid = item?.minBid || itemValue;
      const bidIncrement = item?.bidIncrement || Math.round(itemValue * 0.1);

      data.push({field: 'minBid', value: minBid})
      data.push({field: 'bidIncrement', value: bidIncrement})

      if (info.categories?.length) {
        data.push({field: 'categories', value: info.categories})
      } 

      if (info.desc) {
        data.push({field: 'desc', value: info.desc})
      } 
      
      if (info.images?.length) {
        data.push({ field: 'imageUrl', value: info.images[0] });

        if (info.images.length > 1) {
          data.push({ field: 'additionalImages', value: info.images.slice(1) });
        }
      }

      const buyItNowAmount = item?.buyItNowAmount || itemValue;

      data.push({ 
        field: 'buyItNowEnabled', 
        value: Boolean(item.buyItNowEnabled)
      });

      if (item.buyItNowEnabled) {
        data.push({ field: 'buyItNowAmount', value: buyItNowAmount });
      }

      return data;
    });

    return itemData;
  }

  onClickImport () {
    let options: UntypedFormArray = new UntypedFormArray([], []);
    const data = this.getItemData(); 
    data.unshift({ field: '', value: '' }); // add empty obj as first array since transformTo will skip first row
    
    const result = this.campaignService.transformTo(data, AuctionPresetOptionSchema);

    _.forEach(result, (obj, row) => {
      const option = this.fb.group({});
      this.campaignService.generateForm(option, AuctionPresetOptionSchema);
      this.campaignService.initializeForm(option, obj, AuctionPresetOptionSchema);

      options.push(option);
    });
    
    this.close(options)
  }

  selectAll(): void {
    this.allTransactionList.map(transaction => {
      transaction.checked = true;
      return transaction;
    });

    this.selectedItemsList = this.allTransactionList;
    this.isAllSelected = true;
    this.updateImportButtonState();
  }

  unSelectAll(): void {
    this.allTransactionList.map(transaction => {
      transaction.checked = false;
      return transaction;
    });

    this.selectedItemsList = this.allTransactionList;
    this.isAllSelected = false;
    this.updateImportButtonState();
  }

  onChangeCheckmark(event: {id: string, field: 'checked' | 'buyItNowEnabled'}): void {
    const { id, field } = event;
    const transaction = this.allTransactionList.find(transaction => transaction.id === id);

    if (field === 'checked') {
      transaction.checked = !transaction.checked;
    } else if (field === 'buyItNowEnabled') {
      const enabled = transaction.buyItNowEnabled ? true : false;
      transaction.buyItNowEnabled = !enabled;
      
      if (!transaction.buyItNowEnabled) {
        transaction.buyItNowAmount = 0;
      }
    }

    this.selectedItemsList = this.allTransactionList.filter(transaction => transaction.checked === true);
    this.isAllSelected = this.allTransactionList.every(transaction => transaction.checked);
    this.updateImportButtonState();
  }

  onValueChange(event: {id: string, field: 'minBid' | 'bidIncrement' | 'buyItNowAmount', control: any}): void {
    const transaction = this.allTransactionList.find(t => t.id === event.id);
    if (!transaction) {
      return;
    }

    const value = parseFloat(event.control.value) || 0;

    if (event.field === 'minBid') {
      transaction.minBid = value;
    } else if (event.field === 'bidIncrement') {
      transaction.bidIncrement = value;
    } else if (event.field === 'buyItNowAmount') {
      transaction.buyItNowAmount = value;
    }

    // Update selectedItemsList if this item is selected
    if (transaction.checked) {
      this.selectedItemsList = this.allTransactionList.filter(t => t.checked === true);
    }

    this.updateImportButtonState();
  }

  resetSearch() {
    this.search.setValue('');
  }

  close(data = null) {
    const callback = this.importInKindItemsService.modalRef.component?.data?.callback;

    if (data && callback) {
      callback(data)
    }
    this.importInKindItemsService.close();
  }

  private updateImportButtonState(): void {
    if (this.selectedItemsList.length < 1) {
      this.isImportDisabled = true;
      return;
    }

    const hasCheckedItems = this.allTransactionList.some(item => item.checked);
    if (!hasCheckedItems) {
      this.isImportDisabled = true;
      return;
    }
    
    const hasInvalidInputs = this.selectedItemsList.some(item => {
      const minBid = item?.minBid || 0;
      const bidIncrement = item?.bidIncrement || 0;
      return minBid < AMOUNT_LIMIT.MIN || 
             minBid > AMOUNT_LIMIT.GLOBAL_MAX || 
             bidIncrement < AMOUNT_LIMIT.MIN || 
             bidIncrement > AMOUNT_LIMIT.GLOBAL_MAX;
    });

    const hasEmptyItemNames = this.selectedItemsList.some(item => {
      const itemName = item?.inKindDonationItemInfo?.itemName || '';
      return !itemName.trim();
    });

    const invalidBuyItNowAmount = this.selectedItemsList.some(item => {
      const buyItNowAmount = item?.buyItNowAmount || 0;
      const buyItNowEnabled = item?.buyItNowEnabled || false;
      if (!buyItNowEnabled) {
        return false;
      }
      return buyItNowAmount < AMOUNT_LIMIT.MIN || buyItNowAmount > AMOUNT_LIMIT.GLOBAL_MAX;
    });

    if (hasInvalidInputs || hasEmptyItemNames || invalidBuyItNowAmount) {
      this.isImportDisabled = true;
      return;
    }

    this.isImportDisabled = false;
  }
}
